import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'src/environments/environment';

export interface B2bInterviewVideoRequest {
  videoOrder: number;
  questionText: string;
  explainText: string;
  expectedAnswer?: string;
  duration?: number;
}

export interface CreateInterviewPayload {
  interviewTitle?: string;
  jobPostId: string | number;
  expirationDate: string;
  instructions: string[];
  keywords: string[];
  /** Enum values accepted by backend: EN | AR | FR */
  language: 'EN' | 'AR' | 'FR';
  maxAdditionalAnswers?: number | null;
  additionalAnswers?: number | null;
  additionalAnswerDuration?: number | null;
  allowedLinksCount: number;
  totalSessionDuration?: number;
  videos: B2bInterviewVideoRequest[];
}

export interface UpdateInterviewPayload {
  interviewTitle?: string;
  jobPostId?: string | number;
  positionTitle?: string;
  positionJobLevel?: string;
  expirationDate?: string;
  instructions?: string[];
  keywords?: string[];
  language?: 'EN' | 'AR' | 'FR' | string;
  maxAdditionalAnswers?: number | null;
  additionalAnswers?: number | null;
  additionalAnswerDuration?: number | null;
  allowedLinksCount?: number;
  totalSessionDuration?: number;
  videos?: B2bInterviewVideoRequest[];
}

export interface CreatePublicInterviewPayload {
  positionTitle: string;
  positionJobLevel: string;
  positionDepartment?: string;
  positionJobType?: string;
  jobPostTitle?: string;
  description?: string;
  interviewTitle: string;
  expirationDate: string;
  instructions: string[];
  keywords: string[];
  language: string; // 'ENGLISH' | 'ARABIC'
  allowedLinksCount: number;
  totalSessionDuration?: number;
  additionalAnswers?: number | null;
  additionalAnswerDuration?: number | null;
  videos: B2bInterviewVideoRequest[];
}

export interface JobPostDetailsResponse {
  id: string;
  jobPostTitle?: string;
  positionTitle?: string;
  categories?: Array<{ id?: string; name?: string } | string>;
  companyName?: string;
  companyId?: string;
  jobLevel?: string;
  [key: string]: any;
}

export interface CreateInterviewResponse {
  interview_id: string | number;
  interview_link: string;
  [key: string]: any;
}

export interface InterviewViewQuestion {
  videoId: string;
  url: string | null;
  questionText: string;
  duration: number;
  videoOrder: number;
  explainText: string;
  explainUrl: string | null;
  expectedAnswer: string;
}

export interface InterviewViewResponse {
  id: string;
  interviewTitle?: string;
  jobPostId: string | null;
  jobPostTitle: string | null;
  description: string | null;
  positionTitle: string | null;
  positionDepartment: string | null;
  positionJobLevel: string | null;
  positionJobType: string | null;
  interviewLink: string;
  expirationDate: string;
  instructions: string[];
  keywords: string[];
  language: 'EN' | 'AR' | 'FR';
  uploadStatus: string;
  maxAdditionalAnswers: number;
  creationDate: string;
  totalQuestions: number;
  totalCandidates: number;
  allowedLinksCount: number;
  linksProvided: number;
  totalSessionDuration: number;
  paid: boolean;
  questions: InterviewViewQuestion[];
  type?: string;
}

@Injectable({
  providedIn: 'root',
})
export class InterviewService {
  private readonly baseUrl = environment.apiUrl;

  constructor(private http: HttpClient) {}

  private logMultipartRequest(label: string, payload: unknown, formData: FormData): void {
    console.groupCollapsed(`[InterviewService] ${label}`);
    console.log('JSON payload:', payload);
    const entries: Array<[string, unknown]> = [];
    formData.forEach((value, key) => {
      entries.push([
        key,
        value instanceof Blob
          ? { type: value.type, size: value.size }
          : value,
      ]);
    });
    console.log('FormData entries:', entries);
    console.groupEnd();
  }

  createInterview(payload: CreateInterviewPayload): Observable<CreateInterviewResponse> {
    // Spring Boot @RequestPart("data") expects multipart/form-data
    // with the 'data' part having Content-Type: application/json
    const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });

    const formData = new FormData();
    formData.append('data', jsonBlob);
    // 'videos' (file uploads) intentionally omitted — text-only questions sent inside data JSON

    this.logMultipartRequest('createInterview request', payload, formData);

    return this.http.post<CreateInterviewResponse>(
      `${this.baseUrl}api/b2b/interview/create`,
      formData
    );
  }

  createPublicInterview(payload: CreatePublicInterviewPayload): Observable<any> {
    const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const formData = new FormData();
    formData.append('data', jsonBlob);

    this.logMultipartRequest('createPublicInterview request', payload, formData);

    return this.http.post<any>(
      `${this.baseUrl}api/b2b/interview/create/public`,
      formData
    );
  }

  updateInterview(interviewId: string, payload: UpdateInterviewPayload): Observable<any> {
    const jsonBlob = new Blob([JSON.stringify(payload)], { type: 'application/json' });
    const formData = new FormData();
    formData.append('data', jsonBlob);

    this.logMultipartRequest('updateInterview request', payload, formData);

    return this.http.put<any>(
      `${this.baseUrl}api/b2b/interview/update/${interviewId}`,
      formData
    );
  }

  getJobPostById(id: string | number): Observable<JobPostDetailsResponse> {
    return this.http.get<JobPostDetailsResponse>(
      `${this.baseUrl}api/job-posts/${id}`
    );
  }

  getInterviewById(id: string): Observable<InterviewViewResponse> {
    return this.http.get<InterviewViewResponse>(
      `${this.baseUrl}api/b2b/interview/company/${id}`
    );
  }
}
